<!-- ============================================================
  EventLevelResults.vue — แท็บ "ผลการแข่ง / เลื่อนเลเวล" ในกล่องรายชื่อผู้เข้าร่วม (EventManager)
  ดีไซน์ + ที่เจ้าของเคาะ: C:\iStarApp\LEVEL_PROMOTION_DESIGN.md
  • ข้อมูลมาจาก /getEventLevelResults (พ่อแม่โหลดก่อนเปิด dialog แล้วส่งมาทาง prop `data`)
  • ช่องคะแนนตามชุดอุปกรณ์ของเพศเด็ก · ไม่มีเพศในระบบ → ให้เลือกชุดในแถวนั้น (ไม่เดา)
  • รวม = ผลบวก คำนวณบนจอ ไม่เก็บ · เลเวลใหม่ว่าง = ไม่เปลี่ยน · เปลี่ยนเกิน 1 ระดับ = ต้องยืนยัน
  • ส่งเฉพาะแถวที่แก้จริง (ลด audit ขยะ) · บันทึกเสร็จ emit 'reload' ให้พ่อแม่โหลดใหม่
============================================================ -->
<template>
  <div>
    <div v-if="!data" class="lr-dim">{{ $t('levelRes.loadFail') }}</div>
    <div v-else-if="!data.event.open" class="lr-note">
      <span class="mdi mdi-calendar-clock"></span> {{ $t('levelRes.notYet', { date: $formatShortDate(data.event.eventdate) }) }}
    </div>
    <template v-else>
      <div class="lr-note"><span class="mdi mdi-information-outline"></span> {{ $t('levelRes.hint') }}</div>
      <!-- สรุปของอีเวนต์นี้ (เจ้าของสั่ง 2026-10-26) — นับจากข้อมูลที่บันทึกแล้ว ไม่ใช่ที่กำลังพิมพ์ -->
      <div v-if="rows.length" class="lr-sum">
        <span>{{ $t('levelRes.sumScored', { n: summary.scored, total: rows.length }) }}</span>
        <span class="lr-up"><span class="mdi mdi-arrow-up-bold"></span> {{ $t('levelRes.sumUp', { n: summary.up }) }}</span>
        <span v-if="summary.down" class="lr-down"><span class="mdi mdi-arrow-down-bold"></span> {{ $t('levelRes.sumDown', { n: summary.down }) }}</span>
      </div>
      <!-- 1 แถว = ซ้าย: ชื่อ + คะแนนตามอุปกรณ์ · ขวา (คอลัมน์แยก): เลเวลปัจจุบัน / ส่งแข่ง / เลเวลใหม่ -->
      <div v-for="r in rows" :key="r.registrationid" class="lr-row">
        <div class="lr-main">
          <div class="lr-head">
            <b>{{ r.nickname }}</b> <span class="lr-dim">{{ r.fullname }}</span>
            <span v-for="c in r.changes" :key="c.id" class="lr-tag lr-tag-ok">
              {{ $t('levelRes.changed', { from: c.old_level ?? '-', to: c.new_level ?? '-' }) }}
              <span class="mdi mdi-close-circle-outline lr-void" role="button" :aria-label="$t('levelRes.voidBtn')"
                v-hint="$t('levelRes.voidBtn')" @click="askVoid(r, c)"></span>
            </span>
          </div>
          <div class="lr-body">
            <div v-if="!r.genderKnown" class="lr-set">
              <button v-for="s in ['F', 'M']" :key="s" type="button" class="lr-setbtn" :class="{ on: r.set === s }"
                @click="r.set = s">{{ $t('levelRes.set' + s) }}</button>
            </div>
            <template v-if="r.set">
              <label v-for="a in data.apparatus[r.set]" :key="a" class="lr-score">
                <span>{{ $t('apparatus.' + a) }} ({{ a }})</span>
                <input class="id-input" type="number" min="0" max="999.999" step="0.001" inputmode="decimal"
                  autocomplete="off" v-model="r.scores[a]">
              </label>
              <span class="lr-total">{{ $t('levelRes.total') }} <b>{{ totalOf(r) }}</b></span>
            </template>
            <span v-else class="lr-dim">{{ $t('levelRes.pickSet') }}</span>
          </div>
        </div>
        <div class="lr-side">
          <div class="lr-tags">
            <span class="lr-tag">{{ $t('levelRes.current', { n: r.current_level ?? '-' }) }}</span>
            <span class="lr-tag lr-tag-info">{{ $t('levelRes.entry', { n: r.entry_level ?? '-' }) }}</span>
          </div>
          <label class="lr-score lr-lv">
            <span>{{ $t('levelRes.newLevel') }}</span>
            <span class="lr-lvwrap">
              <input class="id-input" type="number" min="0" max="10" step="1" inputmode="numeric" autocomplete="off"
                v-model="r.new_level" :placeholder="$t('levelRes.noChange')">
              <button type="button" class="id-btn id-btn-ghost id-btn-sm" :disabled="!canPlusOne(r)" @click="plusOne(r)">+1</button>
            </span>
          </label>
          <label v-if="levelChanging(r)" class="lr-score lr-notebox">
            <span>{{ $t('levelRes.note') }}</span>
            <input class="id-input" maxlength="255" autocomplete="off" v-model="r.note">
          </label>
        </div>
      </div>
      <div v-if="rows.length === 0" class="lr-dim">{{ $t('eventMgr.noKidsYet') }}</div>
      <!-- ตรึงไว้ล่างสุดของพื้นที่เลื่อน (sticky ใน .id-modal-body) — กรอกอยู่แถวไหนก็เห็นยอดที่แก้ + ปุ่มบันทึก -->
      <div v-if="rows.length" class="lr-foot" :class="{ dirty: dirtyRows.length > 0 }">
        <span class="lr-dim">{{ $t('levelRes.dirty', { n: dirtyRows.length }) }}</span>
        <button class="id-btn id-btn-primary id-btn-sm" :disabled="saving || dirtyRows.length === 0" @click="trySave">
          <span class="mdi mdi-content-save"></span> {{ $t('levelRes.saveAll') }}
        </button>
      </div>
    </template>

    <id-modal v-model="confirmOpen" size="sm" icon="mdi-alert-outline" :title="$t('levelRes.confirmTitle')" persistent>
      <div class="danger-txt"><span class="mdi mdi-alert-circle-outline"></span> <span>{{ confirmMsg }}</span></div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="confirmOpen = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="confirmGo">
          <span class="mdi mdi-check"></span> {{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'

const blank = (v) => v === '' || v === null || v === undefined
// ลำดับเลเวลสำหรับเทียบขึ้น/ลง — ตัวเลขเท่านั้น · ว่าง/ไม่ใช่ตัวเลข = -1 (ยังไม่มีเลเวล) · คู่กับ levelRank ใน server.js
const levelRank = (v) => { const t = String(v ?? '').trim(); return /^\d+(\.\d+)?$/.test(t) ? Number(t) : -1 }
const lvNum = (v) => (blank(v) || !/^\d+$/.test(String(v).trim()) ? null : Number(v))

export default {
  name: 'EventLevelResults',
  props: { data: { type: Object, default: null }, eventid: { type: [Number, String], default: null } },
  emits: ['reload', 'onErrorHandler', 'onInfoHandler', 'onLoading'],
  data() {
    return { rows: [], saving: false, confirmOpen: false, confirmMsg: '', confirmAction: null }
  },
  computed: {
    dirtyRows() { return this.rows.filter((r) => this.snapshot(r) !== r.orig) },
    // สรุปจากที่บันทึกแล้ว (prop data) — นับ "เด็ก" ด้วยผลสุทธิ (เลเวลเดิมของรายการแรก → เลเวลใหม่ของรายการสุดท้าย)
    // กติกาเดียวกับคอลัมน์ "เลื่อนเลเวล" ของ /getEvents (server.js → levelRank) ห้ามแก้ข้างเดียว
    summary() {
      const res = (this.data && this.data.results) || []
      let up = 0, down = 0
      for (const r of res) {
        const ch = r.changes || []
        if (!ch.length) continue
        const d = levelRank(ch[ch.length - 1].new_level) - levelRank(ch[0].old_level)
        if (d > 0) up++
        else if (d < 0) down++
      }
      return { scored: res.filter((r) => Object.keys(r.scores || {}).length > 0).length, up, down }
    },
  },
  watch: {
    data: { immediate: true, handler(d) { this.rows = (d && d.results ? d.results : []).map((x) => this.toRow(x)) } },
  },
  methods: {
    hdr() { return { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } } },
    toRow(x) {
      const scores = {}
      for (const [k, v] of Object.entries(x.scores || {})) scores[k] = String(Number(v))
      const r = { ...x, set: x.apparatusSet, scores, new_level: '', note: '' }
      r.orig = this.snapshot(r)
      r.origScores = this.scoreKey(r)
      return r
    },
    // คะแนนเฉพาะอุปกรณ์ในชุดปัจจุบัน — สลับชุดแล้วช่องของชุดเก่าต้องไม่ติดไปด้วย
    cleanScores(r) {
      const out = {}
      for (const a of (r.set && this.data.apparatus[r.set]) || []) if (!blank(r.scores[a])) out[a] = String(r.scores[a]).trim()
      return out
    },
    scoreKey(r) { return JSON.stringify([r.set, this.cleanScores(r)]) },
    snapshot(r) { return JSON.stringify([this.scoreKey(r), String(r.new_level ?? '').trim(), r.note]) },
    totalOf(r) {
      const sum = Object.values(this.cleanScores(r)).reduce((s, v) => s + (Number(v) || 0), 0)
      return sum.toFixed(3)
    },
    levelChanging(r) { return !blank(r.new_level) && String(lvNum(r.new_level)) !== String(r.current_level ?? '').trim() },
    canPlusOne(r) { return lvNum(r.current_level) !== null && lvNum(r.current_level) < 10 },
    plusOne(r) { r.new_level = String(lvNum(r.current_level) + 1) },
    trySave() {
      const big = this.dirtyRows.filter((r) => {
        const a = lvNum(r.current_level), b = lvNum(r.new_level)
        return this.levelChanging(r) && a !== null && b !== null && Math.abs(b - a) > 1
      })
      if (big.length === 0) return this.save()
      this.confirmMsg = this.$t('levelRes.bigJump', {
        list: big.map((r) => `${r.nickname} ${r.current_level} → ${lvNum(r.new_level)}`).join(', '),
      })
      this.confirmAction = () => this.save()
      this.confirmOpen = true
    },
    confirmGo() { this.confirmOpen = false; if (this.confirmAction) this.confirmAction() },
    async save() {
      this.saving = true
      this.$emit('onLoading', true)
      try {
        const rows = this.dirtyRows.map((r) => ({
          registrationid: r.registrationid, apparatusSet: r.set || undefined,
          // ส่งคะแนนเฉพาะแถวที่แก้คะแนนจริง — backend แทนที่คะแนนทั้งแถว แถวที่แก้แค่เลเวลจะไม่ลบคะแนนที่ซ่อนอยู่
          // (เด็กไม่มีเพศที่มีแต่ FX/VT เดาชุดไม่ได้ตอนโหลด · อีกแท็บเพิ่งบันทึกคะแนนไป)
          ...(this.scoreKey(r) !== r.origScores ? { scores: this.cleanScores(r) } : {}),
          ...(this.levelChanging(r) ? { new_level: String(lvNum(r.new_level) ?? r.new_level).trim(), note: r.note } : {}),
        }))
        const res = await axios.post(this.baseURL + '/saveEventLevelResults', { eventid: this.eventid, rows }, this.hdr())
        if (res.data.success) { this.$emit('onInfoHandler', res.data.message); this.$emit('reload') }
        else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.response?.data?.message || e.message) } finally {
        this.saving = false
        this.$emit('onLoading', false)
      }
    },
    askVoid(r, c) {
      this.confirmMsg = this.$t('levelRes.voidConfirm', { name: r.nickname, from: c.old_level ?? '-', to: c.new_level ?? '-' })
      this.confirmAction = () => this.doVoid(c)
      this.confirmOpen = true
    },
    async doVoid(c) {
      this.$emit('onLoading', true)
      try {
        const res = await axios.post(this.baseURL + '/voidLevelChange', { eventid: this.eventid, id: c.id }, this.hdr())
        if (res.data.success) { this.$emit('onInfoHandler', res.data.message); this.$emit('reload') }
        else this.$emit('onErrorHandler', res.data.message)
      } catch (e) { this.$emit('onErrorHandler', e.response?.data?.message || e.message) } finally { this.$emit('onLoading', false) }
    },
  },
}
</script>

<style scoped>
.lr-dim { color: var(--c-text-muted); font-size: 12px; }
.lr-note { font-size: 13px; color: var(--c-text-muted); margin: 0 0 10px; display: flex; gap: 6px; align-items: flex-start; }
.lr-sum { display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 13px; margin: 0 0 6px; padding: 8px 12px;
  border-radius: var(--radius-md); background: var(--c-surface-2); }
.lr-up { color: var(--c-success); font-weight: 600; }
.lr-down { color: var(--c-warning); font-weight: 600; }
.lr-row { display: flex; gap: 16px; align-items: flex-start; padding: 10px 4px; border-bottom: 1px solid var(--c-border, #f0eaee); }
.lr-main { flex: 1 1 auto; min-width: 0; }
/* คอลัมน์ขวา: ป้ายเลเวลซ้อนกัน + ช่องเลเวลใหม่ แยกจากช่องคะแนนชัด ๆ (เจ้าของสั่ง 2026-10-26) */
.lr-side { flex: 0 0 176px; display: flex; flex-direction: column; gap: 8px; padding-left: 14px;
  border-left: 1px dashed var(--c-border); }
.lr-tags { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; }
@media (max-width: 600px) {
  .lr-row { flex-direction: column; gap: 10px; }
  .lr-side { flex-basis: auto; width: 100%; padding-left: 0; border-left: 0; }
  .lr-tags { flex-direction: row; }
}
.lr-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.lr-body { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
.lr-tag { font-size: 11px; padding: 2px 8px; border-radius: var(--radius-pill); background: var(--c-surface-2, #f2ecef); color: var(--c-text-muted); }
.lr-tag-info { background: #e3f2fd; color: #1565c0; }
.lr-tag-ok { background: #e8f5e9; color: #2e7d32; display: inline-flex; align-items: center; gap: 4px; }
.lr-void { cursor: pointer; color: var(--c-error); font-size: 14px; }
.lr-score { display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: var(--c-text-muted); }
.lr-score .id-input { width: 92px; }
.lr-notebox .id-input { width: 100%; }
.lr-lvwrap { display: flex; gap: 4px; align-items: center; }
.lr-lvwrap .id-input { width: 80px; }
.lr-total { font-size: 13px; padding-bottom: 8px; min-width: 90px; }
.lr-set { display: flex; gap: 6px; width: 100%; }
.lr-setbtn { appearance: none; border: 1px solid var(--c-border); background: none; border-radius: var(--radius-pill);
  padding: 4px 12px; font-size: 12px; cursor: pointer; color: var(--c-text-muted); }
.lr-setbtn.on { border-color: var(--c-primary); color: var(--c-primary); font-weight: 600; }
/* sticky ภายใน .id-modal-body (ตัวเลื่อนของ dialog) — ติดขอบล่างของพื้นที่ที่มองเห็นเสมอ
   margin ลบ = ยืดเต็มความกว้าง body (padding 20px) และกลืน padding ล่าง 22px ไม่ให้เห็นแถวลอดใต้แถบ */
.lr-foot { position: sticky; bottom: -22px; z-index: 2; display: flex; justify-content: flex-end; align-items: center;
  gap: 10px; margin: 12px -20px -22px; padding: 12px 20px; background: var(--c-surface); border-top: 1px solid var(--c-border); }
.lr-foot.dirty .lr-dim { color: var(--c-warning); font-weight: 600; font-size: 13px; }
</style>
